import { CATCH_GRACE_MS, type HaftkhabisOwe, type HaftkhabisView } from "@bing/engine-haftkhabis";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { playSfxOnTime, preloadSfx, type SfxName } from "../sound/sfx";
import { lifecycle } from "./lifecycle";
import {
  advance,
  EFFECT_MS,
  goneHidden,
  initialFeed,
  momentDone,
  stepTo,
  windowOpened,
  type Feed,
} from "./haftkhabis-effects";
import { settledScene, type Moment, type Scene, type ShownEffect } from "./haftkhabis-moments";
import { HAFTKHABIS_CLIPS } from "./haftkhabis-sfx";

/**
 * Is the app on screen? Hidden = the document is, or the app's fused activity signal says so (Telegram
 * minimised the Mini App, a pagehide, a freeze), which `document.visibilityState` alone misses: «مار»'s and
 * «سودوکو»'s rule.
 */
const pageVisible = (): boolean =>
  (typeof document === "undefined" || document.visibilityState !== "hidden") && lifecycle.appActive;

/** Coming back, the socket may hand over what it held in one go: this long of frames pass as a hidden one's. */
const BACK_QUIET_MS = 1000;
/** A cue this late (a stalled main thread) stays silent: a sound off its moment is worse than none, and several due
 *  at once would be a burst. */
const CUE_LATE_MS = 120;

/** Now, on the scale of `document.timeline`. */
const clockNow = (): number =>
  typeof performance !== "undefined" ? performance.now() : Date.now();

/**
 * The instant a moment's flights and timers all count from: the timeline's own «now», which WAAPI's `startTime` takes as
 * it stands, so a card's landing and its sound are the same instant on one clock. (A timeline that has not ticked for a
 * while, or none at all: the wall of `performance`.)
 */
function timelineNow(): number {
  const now = clockNow();
  const t = typeof document === "undefined" ? null : (document.timeline?.currentTime ?? null);
  return typeof t === "number" && Math.abs(now - t) < 40 ? t : now;
}

export interface MomentClock {
  /** The instant the moment `id` started: its first caller, in that commit, fixes it. */
  readonly t0: (id: string) => number;
}

export interface HaftkhabisFeed {
  /** What the table draws now. */
  readonly scene: Scene;
  /** The moment on screen, or null. */
  readonly moment: Moment | null;
  /** A move's moment is on screen or waiting: nothing may be played. A side move's moves no turn and holds nothing. */
  readonly busy: boolean;
  /** …and it is a round's opening or its end: the turn shows nowhere. */
  readonly opening: boolean;
  /** A round's end is on screen or waiting: the end card waits for it. */
  readonly holding: boolean;
  /** The banner on the row, with the mark it lands: it outlives its moment. */
  readonly banner: ShownEffect | null;
  readonly clock: MomentClock;
}

/**
 * THE TABLE'S ONE FEED — `advance`'s result held in state and its one timer run: a timer exists only because a frame
 * brought a moment, one at a time to the next thing on its timeline (a sound or a scene), and it is gone when the moment
 * is. Going hidden drops all of it (`goneHidden`), and frames that land while hidden, or in the first second back, pass
 * silently: no sound, no flight, nothing owed on return. No interval, no animation frame, no poll.
 *
 * SOUNDS FIRE HERE, off the moment's clock, never off a frame's arrival: a moment's `cues` are the card's landing less
 * the clip's lead-in. Set during render, as `useLaidRoom`'s and the capture hold's state is: a frame must never paint its
 * new table for one frame before its moment latches.
 */
export function useHaftkhabisFeed(
  view: HaftkhabisView,
  /** The `turn` frame of a window that is yours, by identity: a new one means your clock has started. */
  yourWindow: object | null = null,
  /** A re-join onto a table in progress: a round's first frame here is no deal to replay. */
  resumed = false,
): HaftkhabisFeed {
  const [state, setState] = useState<Feed>(() => initialFeed(view, pageVisible() && !resumed));
  const backAt = useRef(0);
  let feed = state;
  if (state.frame !== view) {
    feed = advance(state, view, pageVisible() && Date.now() - backAt.current >= BACK_QUIET_MS);
    setState(feed);
  }

  // THE BANNER: up as its scene's time comes, for its time or until the next. Its own timer, cleared when it goes.
  const [banner, setBanner] = useState<ShownEffect | null>(null);
  const bannerTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const bannerShown = useRef<ShownEffect | null>(null);
  const dropBanner = useCallback((): void => {
    if (bannerTimer.current !== null) clearTimeout(bannerTimer.current);
    bannerTimer.current = null;
    setBanner(null);
  }, []);
  const showBanner = useCallback((effect: ShownEffect | null): void => {
    if (effect === null || effect === bannerShown.current) return;
    bannerShown.current = effect;
    if (bannerTimer.current !== null) clearTimeout(bannerTimer.current);
    setBanner(effect);
    bannerTimer.current = setTimeout(() => {
      bannerTimer.current = null;
      setBanner(null);
    }, EFFECT_MS);
  }, []);

  // The app went away: nothing waits for it. Listeners cost nothing while nothing changes.
  useEffect(() => {
    let away = !pageVisible();
    const onChange = (): void => {
      const now = !pageVisible();
      if (now) {
        setState(goneHidden);
        dropBanner();
      } else if (away) backAt.current = Date.now();
      away = now;
    };
    document.addEventListener("visibilitychange", onChange);
    const off = lifecycle.subscribe(onChange);
    return () => {
      document.removeEventListener("visibilitychange", onChange);
      off();
    };
  }, [dropBanner]);

  // Every clip warmed once, so the first of each is on its moment too (a clip's first play would otherwise fetch it).
  useEffect(() => preloadSfx(HAFTKHABIS_CLIPS), []);

  // Your clock has started: nothing of the opening holds your hand any more. Keyed on the frame's identity,
  // so the last window's frame, still in hand when a round ends, cannot end that round's card.
  useEffect(() => {
    if (yourWindow !== null) setState(windowOpened);
  }, [yourWindow]);

  const head = feed.queue[0] ?? null;
  const headId = head?.id ?? null;
  const live = useMemo(() => settledScene(feed.frame, feed.under), [feed.frame, feed.under]);
  const scene = head ? (head.scenes[feed.step]?.scene ?? head.scenes[0]!.scene) : live;

  // THE ONE CLOCK: a child's layout effect (its flight) and this hook's agree on the moment's first instant.
  const t0Ref = useRef<{ id: string; at: number } | null>(null);
  const clock = useMemo<MomentClock>(
    () => ({
      t0: (id) => {
        if (t0Ref.current?.id !== id) t0Ref.current = { id, at: timelineNow() };
        return t0Ref.current.at;
      },
    }),
    [],
  );

  // THE MOMENT'S ONE TIMER, to the next thing on its timeline. Keyed on the moment's id, so a new frame neither restarts
  // nor ends it.
  const momentRef = useRef(head);
  momentRef.current = head;
  useLayoutEffect(() => {
    const moment = momentRef.current;
    if (moment === null) return undefined;
    const { id } = moment;
    const t0 = clock.t0(id);
    type Beat =
      | { readonly at: number; readonly sfx: SfxName }
      | { readonly at: number; readonly step: number }
      | { readonly at: number; readonly end: true };
    const beats: Beat[] = [
      ...moment.cues.map((c) => ({ at: c.at, sfx: c.sfx })),
      ...moment.scenes.slice(1).map((s, i) => ({ at: s.at, step: i + 1 })),
      { at: moment.ms, end: true as const },
    ];
    // stable: at one instant, scenes as they were pushed; the end last
    beats.sort((a, b) => a.at - b.at);
    let at = 0;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let step = 0;
    // a timer never fires before its time, so what it was set for has passed, whatever a clock says
    let due = 0;
    const run = (): void => {
      timer = null;
      const elapsed = Math.max(clockNow() - t0, due);
      let ended = false;
      while (at < beats.length && beats[at]!.at <= elapsed) {
        const beat = beats[at]!;
        at += 1;
        if ("sfx" in beat) {
          if (elapsed - beat.at <= CUE_LATE_MS) playSfxOnTime(beat.sfx);
        } else if ("step" in beat) step = beat.step;
        else ended = true;
      }
      // the banner off the timeline, so a scene that ends its moment still says what it has to say
      showBanner(moment.scenes[step]?.scene.effect ?? null);
      if (ended) setState((f) => momentDone(f, id));
      else if (step > 0) setState((f) => stepTo(f, id, step));
      const next = beats[at];
      if (next !== undefined && !ended) {
        due = next.at;
        timer = setTimeout(run, Math.max(0, next.at - elapsed));
      }
    };
    run();
    return () => {
      if (timer !== null) clearTimeout(timer);
    };
  }, [headId, clock, showBanner]);

  useEffect(
    () => () => {
      if (bannerTimer.current !== null) clearTimeout(bannerTimer.current);
    },
    [],
  );

  return {
    scene,
    moment: head,
    busy: feed.queue.some((m) => m.kind !== "press" && m.kind !== "catch"),
    opening: head?.opening === true,
    holding: feed.queue.some((m) => m.kind === "roundEnd"),
    banner,
    clock,
  };
}

/**
 * `useFrameKey`, but a frame that says only that somebody pressed or caught is no new frame of YOURS: it answers no send
 * and ends no choice, so a card you lifted stays lifted, a card you sent stays up, and it is sent once.
 */
export function useMoveFrameKey(view: HaftkhabisView): number {
  const ref = useRef({ view, key: 0 });
  if (ref.current.view !== view) {
    const fresh = view.events.filter((e) => e.seq > ref.current.view.eventSeq);
    const side = fresh.length > 0 && fresh.every((e) => e.kind === "press" || e.kind === "catch");
    ref.current = { view, key: ref.current.key + (side ? 0 : 1) };
  }
  return ref.current.key;
}

/**
 * THE SEATS YOU MAY CATCH (D16): another seat that owes «تک کارت», from `CATCH_GRACE_MS` after the frame
 * that first carried that descent ({seat, seq}) until the window closes. The server refuses earlier, so
 * «تک کارت نگفت!» and its tap appear together; a frame reaches a client after the server stamped it, so
 * the client's two seconds never start before the server's. One timer, for the soonest descent still
 * waiting, cleared when the list changes; none while the app is hidden (it comes back read off the same
 * stamps). A descent seen on a first frame is as old as that frame: it waits its two seconds.
 */
export function useCatchable(owes: readonly HaftkhabisOwe[], you: number): ReadonlySet<number> {
  const seenAt = useRef(new Map<string, number>());
  const [ready, setReady] = useState<readonly string[]>([]);
  const others = owes.filter((o) => o.seat !== you);
  const key = others.map((o) => `${o.seat}:${o.seq}`).join(",");
  const [tick, setTick] = useState(0);
  useEffect(() => {
    const now = Date.now();
    const keys = key === "" ? [] : key.split(",");
    for (const k of [...seenAt.current.keys()]) if (!keys.includes(k)) seenAt.current.delete(k);
    for (const k of keys) if (!seenAt.current.has(k)) seenAt.current.set(k, now);
    const due = keys.filter((k) => now - (seenAt.current.get(k) ?? now) >= CATCH_GRACE_MS);
    setReady((cur) => (cur.join() === due.join() ? cur : due));
    const waits = keys
      .filter((k) => !due.includes(k))
      .map((k) => (seenAt.current.get(k) ?? now) + CATCH_GRACE_MS - now);
    if (waits.length === 0) return undefined;
    if (!pageVisible()) {
      const back = (): void => {
        if (pageVisible()) setTick((t) => t + 1);
      };
      document.addEventListener("visibilitychange", back);
      const off = lifecycle.subscribe(back);
      return () => {
        document.removeEventListener("visibilitychange", back);
        off();
      };
    }
    const timer = setTimeout(() => setTick((t) => t + 1), Math.min(...waits));
    return () => clearTimeout(timer);
  }, [key, tick]);
  // `ready` lags the frame by an effect: never offer a descent the frame no longer carries (the same
  // seat's NEW descent is another key: it waits its own two seconds).
  return new Set(others.filter((o) => ready.includes(`${o.seat}:${o.seq}`)).map((o) => o.seat));
}
